import { useCallback } from 'react'

import {
  paymentDetail,
  wireReceiveDetail,
  wireWithdrawalDetail,
} from '@services/sections/lib/payments'
import { ComplexTable, TableCard } from '@shared/components/tables'
import { FeeActivity, useFeeReportTransactionsLazyQuery } from '@shared/graphql'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { useRelatedTransactionsColumns } from './RelatedTransactionsTable.Columns'

import type {
  OnFetchDataProps,
  Row,
} from '@circlefin/components/lib/AdvancedTable'
import type { Section } from '@services/sections/types'
import type { FeeReport, InvoiceItem } from '@shared/graphql'

const { Header, Body } = TableCard

const routeSectionMap: Record<FeeActivity, Section> = {
  [FeeActivity.cpnPayment]: paymentDetail,
  [FeeActivity.pushPayment]: wireReceiveDetail,
  [FeeActivity.payout]: wireWithdrawalDetail,
  [FeeActivity.returnedPayout]: wireReceiveDetail,
} as const

export interface RelatedTransactionsTableProps {
  /**
   * Fee report.
   */
  feeReport: FeeReport
  /**
   * Controls the amount of lines in the table for each paginated page.
   */
  pageSize?: number
}

export const RelatedTransactionsTable: React.FC<
  RelatedTransactionsTableProps
> = ({ feeReport, pageSize = 10 }) => {
  const { t } = useTranslation('settings')
  const router = useRouter()

  const [fetchTransactions, { called, data, loading, error, refetch }] =
    useFeeReportTransactionsLazyQuery({
      variables: {
        id: feeReport.id,
        page: { pageSize },
      },
      fetchPolicy: 'cache-and-network',
    })

  const onRowClick = useCallback(
    ({ original }: Row<InvoiceItem>) => {
      void router.push({
        pathname: routeSectionMap[original.activity].route,
        query: {
          id: original.id,
        },
      })
    },
    [router],
  )

  const columns = useRelatedTransactionsColumns()

  const handleFetchData = useCallback(
    ({ currentPageLink }: OnFetchDataProps<InvoiceItem>) => {
      void fetchTransactions({
        variables: {
          page: currentPageLink
            ? {
                pageSize: currentPageLink.pageSize,
                from: currentPageLink.from,
                to: currentPageLink.to,
                pageAfter: currentPageLink.pageAfter,
                pageBefore: currentPageLink.pageBefore,
              }
            : { pageSize },
          id: feeReport.id,
        },
      })
    },
    [fetchTransactions, pageSize, feeReport.id],
  )

  return (
    <TableCard paddingVariant="sm">
      <Header>
        <span className="pl-4 type-h-title-md">
          {t('billing.feeDetails.table.title')}
        </span>
      </Header>
      <Body>
        <ComplexTable<InvoiceItem>
          columns={columns}
          data={data?.feeReportTransactions ?? []}
          data-testid="daily-fees-table"
          empty={{ subtitle: t('billing.feeDetails.table.empty') }}
          enableSorting={false}
          error={error}
          loading={!called || loading}
          onFetchData={handleFetchData}
          onRowClick={onRowClick}
          pageSize={pageSize}
          retry={refetch}
          showHeader={false}
        />
      </Body>
    </TableCard>
  )
}
